import { z } from 'zod';

import { type ApiError } from '../../common/envelope/envelope';

/**
 * The stable error code every fail-closed AI refusal carries (N15).
 *
 * One code, many reasons: a consumer's declared degradation is the same
 * whichever guard fired — moderation queues the message for human review,
 * face-match leaves the step pending with a visible note — so branching on the
 * code is what a caller does, and the reason is what an operator reads.
 */
export const AI_UNAVAILABLE_CODE = 'ai_unavailable';

/** The `details` key carrying the reason (the envelope's map is `Record<string, string[]>`). */
export const AI_UNAVAILABLE_DETAIL_KEY = 'aiUnavailable';

/**
 * Why the platform refused.
 *
 * Every one of these is a **refusal, not a skip** — nothing anywhere pretends
 * the AI ran (`03-ai-platform.md` §guards). The distinctions matter to whoever
 * has to fix it:
 *
 * - `rate_limited`     — this member/feature exceeded `ai.rateLimit.perUserPerHour`.
 * - `quota_exceeded`   — the platform exceeded `ai.quota.dailyCalls`.
 * - `budget_exceeded`  — the platform exceeded `ai.budget.monthlyUsd`.
 * - `guard_unavailable`— neither Redis nor Postgres could answer what has been
 *                        spent, so no guard could prove the call is within
 *                        limits. Distinct from the three above because it is an
 *                        infrastructure fault, not a policy decision.
 * - `not_configured`   — no active provider for the capability, or a required
 *                        vault field resolved nowhere (N21).
 * - `unsupported_capability` — the active provider cannot perform this
 *                        operation at all (Anthropic has no embeddings API).
 * - `provider_error`   — the vendor rejected the call or failed.
 * - `invalid_response` — the vendor answered, but not with something the
 *                        platform can vouch for: a refusal stop reason, a
 *                        truncated completion, or a payload that fails its
 *                        schema. Never downgraded into a low-confidence result.
 */
export const AI_UNAVAILABLE_REASONS = [
  'rate_limited',
  'quota_exceeded',
  'budget_exceeded',
  'guard_unavailable',
  'not_configured',
  'unsupported_capability',
  'provider_error',
  'invalid_response',
] as const;

export const AiUnavailableReasonSchema = z.enum(AI_UNAVAILABLE_REASONS);

export type AiUnavailableReason = (typeof AI_UNAVAILABLE_REASONS)[number];

const REASON_SET: ReadonlySet<string> = new Set<string>(AI_UNAVAILABLE_REASONS);

/** Narrows a stored `ai_usage_logs.refusal_reason` or an envelope detail. */
export function isAiUnavailableReason(value: string): value is AiUnavailableReason {
  return REASON_SET.has(value);
}

/**
 * Whether an error envelope is the AI platform refusing.
 *
 * The status is deliberately not part of the test — the code is the contract
 * (the `isConsentRequired` precedent).
 */
export function isAiUnavailable(error: ApiError): boolean {
  return error.code === AI_UNAVAILABLE_CODE;
}

/** The reason named by an AI refusal, or `null` when the envelope is not one. */
export function aiUnavailableReasonOf(error: ApiError): AiUnavailableReason | null {
  if (!isAiUnavailable(error)) {
    return null;
  }

  const reason = error.details?.[AI_UNAVAILABLE_DETAIL_KEY]?.[0];

  return reason !== undefined && isAiUnavailableReason(reason) ? reason : null;
}
